import { IScheduledTask } from 'src/generated-types/scheduledTask'
import { taskQueryKeys } from 'src/query-keys'
import { IResource } from '../generated-types/resource'
import { ITask } from '../generated-types/task'
import { queryClient } from '../query-client'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { QueryConfig, useDelete, useGet, usePost, usePut } from './reactQuery'
import { useMutation } from 'react-query'
import { requestProvider } from './requestProvider'
import { TaskFilterQueryParams } from 'src/screens/customer/tasks/tasks/WorkOrderOccurrencesTab.action'

const taskCustomerAdminBaseURL = (customerId: EntityId) => `customers/${customerId}`
const taskBaseURL = (localityId: EntityId) => `localities/${localityId}/task`

export const taskEndpoints = {
  getTaskResources(localityId: EntityId, taskId: EntityId) {
    return `${taskBaseURL(localityId)}/${taskId}/taskWithTaskResources`
  },
  filterTask(customerId) {
    return `${taskCustomerAdminBaseURL(customerId)}/filteredTasks`
  },
  filterScheduleTask(customerId) {
    return `${taskCustomerAdminBaseURL(customerId)}/filteredScheduledTasks`
  },
  filterCalendarScheduleTask(customerId) {
    return `${taskCustomerAdminBaseURL(customerId)}/work-orders/calendar`
  },
  baseURL(localityId) {
    return `${taskCustomerAdminBaseURL(localityId)}/tasks`
  },
  singleTask(localityId, taskId) {
    return `${taskBaseURL(localityId)}/${taskId}`
  },
  localityTaskBaseURL(localityId: EntityId) {
    return `${taskBaseURL(localityId)}`
  },
  unAssignResource(localityId: EntityId, taskId: EntityId) {
    return `${taskBaseURL(localityId)}/${taskId}/unAssignTaskResource`
  },
  assignTaskResources(localityId: EntityId, taskId: EntityId) {
    return `${taskBaseURL(localityId)}/${taskId}/assign-resource`
  },
  validateVesselTimeline(localityId: EntityId) {
    return `${taskBaseURL(localityId)}/validate-vessel-timeline`
  },
  updateTaskResource(localityId: EntityId, taskId: EntityId, taskResourceId: EntityId) {
    return `${taskBaseURL(localityId)}/${taskId}/task-resource/${taskResourceId}`
  },
  revertTaskResource(localityId: EntityId, taskId: EntityId) {
    return `${taskBaseURL(localityId)}/${taskId}/revert-task-resource`
  },
}

export const invalidateTaskCache = {
  useGetTaskResources(localityId: EntityId, taskId: EntityId) {
    queryClient.invalidateQueries(taskEndpoints.getTaskResources(localityId, taskId))
  },
  getCustomerAdminTaskByFilter(customerId) {
    queryClient.invalidateQueries(taskEndpoints.filterTask(customerId))
  },
  getCustomerAdminScheduleTaskByFilter(customerId) {
    queryClient.invalidateQueries(taskEndpoints.filterScheduleTask(customerId))
  },
  getTaskResources(localityId, taskId) {
    queryClient.invalidateQueries(taskEndpoints.getTaskResources(localityId, taskId))
  },
  useGetTask(localityId: EntityId, taskId: EntityId) {
    queryClient.invalidateQueries(taskEndpoints.singleTask(localityId, taskId))
  },
  useGetFilterCalendarScheduleTask(customerId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_CALENDAR_SCHEDULE_TASK(customerId))
  },
}

export const useCreateTask = (localityId: EntityId) => usePost(taskEndpoints.baseURL(localityId))

export const useUpdateTask = (localityId: EntityId, taskId: EntityId) =>
  usePut(taskEndpoints.singleTask(localityId, taskId))

export const useGetFilterTask = (
  customerId: number,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  queries,
) => useGet<IPaginatedResult<ITask>>(taskEndpoints.filterTask(customerId), { page, pageSize, ...queries })

export const useGetFilterScheduleTask = (
  customerId: number,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  queries,
  config?: QueryConfig<IPaginatedResult<IScheduledTask>>,
) =>
  useGet<IPaginatedResult<IScheduledTask>>(
    taskEndpoints.filterScheduleTask(customerId),
    { page, pageSize, ...queries },
    config,
  )

export const useGetFilterCalendarScheduleTask = (
  customerId: number,
  queries: TaskFilterQueryParams,
  config?: QueryConfig<Array<IScheduledTask>>,
) =>
  useGet<Array<IScheduledTask>>(
    taskEndpoints.filterCalendarScheduleTask(customerId),
    queries,
    {
      queryKey: taskQueryKeys.GET_CALENDAR_SCHEDULE_TASK(customerId, queries),
      ...config,
    },
  )

export const useGetTask = (localityId: EntityId, taskId: EntityId) =>
  useGet<ITask>(taskEndpoints.singleTask(localityId, taskId), undefined, { enabled: !!taskId || !!localityId })

export const useDeleteTaskWithParams = (localityId: EntityId, taskId: EntityId) =>
  useMutation((keepOngoingScheduledTasks: boolean) =>
    requestProvider.delete(`${taskBaseURL(localityId)}/${taskId}`, { keepOngoingScheduledTasks })
  )

export const useDeleteTask = (localityId: EntityId) =>
  useDelete<IPaginatedResult<ITask>>(taskEndpoints.localityTaskBaseURL(localityId))

export const useGetTaskResources = (localityId: EntityId, taskId: EntityId, scheduleTaskId?: EntityId) =>
  useGet<Array<IResource>>(taskEndpoints.getTaskResources(localityId, taskId), { scheduleTaskId })

export const useUnAssignTaskResource = (localityId: EntityId, taskId: EntityId) =>
  useDelete<any>(taskEndpoints.unAssignResource(localityId, taskId))

export const useAssignTaskResource = (localityId: EntityId, taskId: EntityId) =>
  usePost(taskEndpoints.assignTaskResources(localityId, taskId))

export const useValidateVesselTimeline = (localityId: EntityId) =>
  usePost(taskEndpoints.validateVesselTimeline(localityId))

export const useUpdateTaskResource = (localityId: EntityId, taskId: EntityId) =>
  useMutation(({ taskResourceId, ...payload }: { taskResourceId: EntityId } & Record<string, unknown>) =>
    requestProvider.put(taskEndpoints.updateTaskResource(localityId, taskId, taskResourceId), payload),
  )

export const useRevertTaskResource = (localityId: EntityId, taskId: EntityId) =>
  useDelete<any>(taskEndpoints.revertTaskResource(localityId, taskId))
